import React, { useEffect, useRef, useState } from 'react';
import { InfoOutlineIcon } from '@chakra-ui/icons';
import {
  Box,
  Center,
  Flex,
  Heading,
  IconButton,
  Image,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { createCustomWifiIcon } from 'clipsal-cortex-icons/src/custom-dynamic-icons';
import { CenteredLoader } from 'clipsal-cortex-ui/src/components/CenteredLoader';

import { useReduxDispatch } from '../../../app/store';
import chargerOfflineImg from '../../../assets/images/wifi_offline.svg';
import chargerOnlineImg from '../../../assets/images/wifi_online.svg';
import { BoltLoader } from '../../../common/components/BoltLoader';
import { SlidingAnimationPageBase } from '../../../common/components/SlidingAnimationPageBase';
import { useParamBackUrl } from '../../../common/hooks/use-param-back-url';
import { trackWifiChangeStarted } from '../../../utils/common/analytics';
import { trackDynatraceWifiChangeStarted } from '../../../utils/native/dynatrace-events';
import { fetchEvChargerDetails, fetchEvChargerStatus, selectEvChargerData } from '../dashboard/evChargerSlice';
import { OFFLINE_EV_CHARGER_STATUSES } from '../ev-charger-helper';
import { setIsEditing } from './wifiConfigSlice';

type WifiConfigurationNavigationState = {
  showConnectionStatusLoader?: boolean;
};

// Safety cap for the post-edit connection loader: stop waiting for the charger to
// come back online via the SSE stream after 2 minutes and reveal the page regardless.
const CONNECTION_STATUS_LOADER_TIMEOUT_MS = 2 * 60 * 1000;
const OFFLINE_STATUSES = [...OFFLINE_EV_CHARGER_STATUSES, 'Faulted'];

/* istanbul ignore next -- @preserve */
export const DeviceWifiConfiguration = () => {
  const { siteDeviceId, hasFetchedEvChargerDetails, status } = useSelector(selectEvChargerData);
  const dispatch = useReduxDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const backURL = useParamBackUrl('../device-details');
  const [isCheckingConnectionStatus, setIsCheckingConnectionStatus] = useState(false);
  const hasCompletedFreshStatusCheckRef = useRef(false);

  const bgColor = useColorModeValue('customGrey.700', 'customGrey.100');
  const textColor = useColorModeValue('customGrey.100', 'customGrey.700');
  const isOnline = !OFFLINE_STATUSES.includes(status);
  const navigationState = (location.state as WifiConfigurationNavigationState | null) ?? null;
  const shouldShowConnectionStatusLoader = navigationState?.showConnectionStatusLoader === true;
  const wifiImg = isOnline ? chargerOnlineImg : chargerOfflineImg;
  const WifiIcon = createCustomWifiIcon(undefined, '');

  // Ensure charger details are loaded so the page can render its content.
  useEffect(() => {
    if (!hasFetchedEvChargerDetails && siteDeviceId) {
      dispatch(fetchEvChargerDetails(siteDeviceId));
    }
  }, [hasFetchedEvChargerDetails, siteDeviceId, dispatch]);

  // When arriving from a successful Wi-Fi edit, show the BoltLoader until the charger
  // reports online via the SSE stream (status is kept live in Redux by useEVSEStream at
  // the EVChargerHome level), or until a 2-minute safety timeout elapses.
  useEffect(() => {
    if (!shouldShowConnectionStatusLoader) {
      return;
    }

    hasCompletedFreshStatusCheckRef.current = false;
    let isCancelled = false;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const startConnectionCheck = async () => {
      setIsCheckingConnectionStatus(true);

      try {
        if (siteDeviceId) {
          // Fix, first force a fresh status fetch so this
          // page does not make a decision from stale Redux state.
          const latestStatus = await dispatch(fetchEvChargerStatus(siteDeviceId)).unwrap();
          const isFreshStatusOnline = !OFFLINE_STATUSES.includes(latestStatus.status);

          if (!isCancelled && isFreshStatusOnline) {
            hasCompletedFreshStatusCheckRef.current = true;
            setIsCheckingConnectionStatus(false);
            navigate('../wifi-configuration-success', { replace: true });
            return;
          }
        }
      } catch {
        // Fall back to SSE-driven updates and timeout below if polling fails.
      }

      hasCompletedFreshStatusCheckRef.current = true;

      timeoutId = setTimeout(() => {
        setIsCheckingConnectionStatus(false);
      }, CONNECTION_STATUS_LOADER_TIMEOUT_MS);
    };

    startConnectionCheck();

    return () => {
      isCancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [shouldShowConnectionStatusLoader, siteDeviceId, dispatch, navigate]);

  // After the initial fresh-status fetch, keep listening for live SSE status updates.
  useEffect(() => {
    if (
      !shouldShowConnectionStatusLoader ||
      !isCheckingConnectionStatus ||
      !hasCompletedFreshStatusCheckRef.current ||
      !isOnline
    ) {
      return;
    }

    setIsCheckingConnectionStatus(false);
    navigate('../wifi-configuration-success', { replace: true });
  }, [shouldShowConnectionStatusLoader, isCheckingConnectionStatus, isOnline, navigate]);

  const handleWifiEdit = () => {
    trackWifiChangeStarted();
    trackDynatraceWifiChangeStarted();
    dispatch(setIsEditing(true));
    // Pass the back target explicitly so the setup flow returns to Wi-Fi settings in the edit flow
    // (rather than inferring it from the global isEditing flag, which is never reset).
    navigate('../device-unplug-ev?setupBackURL=../device-wifi-configuration');
  };

  const boxProps = {
    bg: '#E7E6E6',
    _dark: {
      bg: 'customGrey.800',
    },
    py: 6,
    px: 4,
    display: 'flex',
    position: 'relative' as const,
  };

  return (
    <SlidingAnimationPageBase title={t('Hardware.hardware')} backURL={backURL} customNavigationDirection="forward">
      {!hasFetchedEvChargerDetails ? (
        <CenteredLoader text={`${t('Common.loading')}...`} />
      ) : (
        <Flex direction={'column'} w="100%">
          <Center flexDirection="column" px={3} pb={6} textAlign="center">
            <Box position={'relative'}>
              <Image src={wifiImg} alt="Wi-Fi Connection" mb={4} />
            </Box>

            <Heading mt={2}>{t('EVSE.wifi connection')}</Heading>
            <Text mt={1}>{t('EVSE.wifi connection text')}</Text>
          </Center>

          <Flex direction={'column'} px={4}>
            <Text mb={1}>{t('Hardware.connection')}</Text>
            <Box {...boxProps} justifyContent="space-between" borderTopRightRadius={8} borderTopLeftRadius={8}>
              <Text data-testid="connection-status">
                {isOnline ? t('EVSE.wifi connection online') : t('EVSE.wifi connection offline')}
              </Text>
              <WifiIcon boxSize={5} mr={3} />
              <Box
                left={4}
                bottom={0}
                right={0}
                position={'absolute'}
                w={'calc(100% - 1rem)'}
                h="1px"
                bg={'rgba(151, 151, 151, 0.3)'}
              />
            </Box>
            <Box
              {...boxProps}
              borderBottomLeftRadius={8}
              borderBottomRightRadius={39}
              mt={0}
              data-testid="edit-wifi-button"
              onClick={handleWifiEdit}
            >
              {t('EVSE.edit wifi network')}
              <Popover>
                <PopoverTrigger>
                  <IconButton
                    height={'1em'}
                    min-width={'auto'}
                    size={'lg'}
                    aria-label={'info'}
                    variant={'ghost'}
                    icon={<InfoOutlineIcon />}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => e.stopPropagation()}
                  />
                </PopoverTrigger>
                <PopoverContent borderRadius={8} w="210px" border="none" bg={bgColor} textColor={textColor}>
                  <PopoverBody
                    borderRadius="14px"
                    boxShadow="rgba(0, 0, 0, 0.3) 0px 0px 10px 0px"
                    paddingTop="1em"
                    fontSize={'12px'}
                  >
                    {t('EVSE.edit wifi network info')}
                  </PopoverBody>
                </PopoverContent>
              </Popover>
            </Box>
          </Flex>
        </Flex>
      )}

      {isCheckingConnectionStatus && (
        <BoltLoader description={t('EVSE.connecting to wifi after edit')} testId="wifi-status-loader" />
      )}
    </SlidingAnimationPageBase>
  );
};
